import React, { useContext, useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { useGlobalStore } from "~/store";
import { useTranslation } from "react-i18next";
import { dateToString } from "~/utils/formatter";
import cryptoData from "~/utils/cryptoData";
import moment from "moment";

// Components
import useAlert from "~/common/hooks/useAlert";
import useRequest from "~/common/hooks/useRequest";
import { API_STATE } from "~/common/constants/state";
import { Case, If, OtherWise } from "~/common/components/If";
import Form, { Group } from "~/common/components/Form";
import Input from "~/common/components/Input";
import DatePicker from "~/common/components/DatePicker";

// Assets
import darkRoundThumb from "~/assets/img/defaultImg/dark-default-rec.png";
import lightRoundThumb from "~/assets/img/defaultImg/light-default-rec.png";
import { CautionIcon, RoundCopyIcon, ShareIcon } from "~/assets/svg/InterfaceIcon";

import { AppManContext } from "../RoundManContext";
import LoadingPage from "~/pages/LoadingPage";
import RoundQuest from "~/pages/round/RoundList/component/RoundQuest";
import RoundDetail from "~/pages/round/RoundList/component/RoundDetail";
import ModalEvalSetting from "../modal/evaluationSetting/ModalEvalSetting";
import ModalFinish from "../modal/evaluationSetting/ModalFinish";
import ModalAdditionEval from "../modal/additionalRound/ModalAdditionEval";
import Radio from "~/common/components/Radio";
import Pagination from "~/common/components/Pagination";
import { ROUND_BTN_STATUS, ROUND_BTN_TEXT, dateTypeOptions } from "~/constant/RoundManageConstant";

const RoundManageAll = ({ viewEvalPage, currentTab, currentPath, showAuthAlert }) => {
	const { t } = useTranslation();
	const [roundDetailModal, setRoundDetailModal] = useState(false);

	const [
		{
			theme: { theme },
		},
	] = useGlobalStore();

	const navigate = useNavigate();

	const {
		ref,
		roundList,
		setRoundList,
		loading,
		evaluateSetupModal,
		setEvaluateSetupModal,
		setTargetRoundIdx,
		setTidx,
		setFin,
		setAddJudgeModal,
		setAddEvalTarget,
		formData,
		setFormData,
		totalPage,
	} = useContext(AppManContext);

	const roundListRequest = useRequest("post", "/vc/round/search");
	const viewRequest = useRequest("get", "vc/round/detail");
	const judgeEndRequest = useRequest("post", "vc/round/close");

	const childRoundList = roundList?.filter((d) => d.prevIdx);
	const parentList = roundList?.filter((d) => !d.prevIdx);

	const [roundDetailData, setRoundDetailData] = useState({});
	const [parentRoundList, setParentRoundList] = useState([]);
	const [dateReadOnly, setDateReadOnly] = useState(true);

	useEffect(() => {
		switch (roundListRequest.state) {
			case API_STATE.done:
				const roundListData = roundListRequest.response.data;
				setRoundList(roundListData.list);
				break;
			case API_STATE.error:
				console.log("roundListRequest error>> ", roundListRequest.error);
				alert(t("pages.round.RoundManage.RoundManContext.alert01"));
				break;
			default:
				break;
		}
	}, [roundListRequest.state]);

	useEffect(() => {
		const parentList = [];
		roundList.forEach((round) => {
			round.prevIdx && parentList.push(round.prevIdx);
		});
		currentTab === "전체" && setParentRoundList([...new Set(parentList)]);
	}, [roundList]);

	useEffect(() => {
		switch (viewRequest.state) {
			case API_STATE.done:
				const requestCompanyView = viewRequest.response?.data;
				setRoundDetailData(requestCompanyView);
				setRoundDetailModal(true);
				break;
			default:
				break;
		}
	}, [viewRequest.state, viewRequest.response, roundDetailData]);

	const viewDetail = (idx) => {
		viewRequest.call(idx, "path");
	};

	const [inquiryPreModal, setInquiryPreModal] = useState(false);
	const inquiryOutputForm = useForm();

	const searchForm = useForm({
		defaultValues: {
			dateType: "1",
			startDate: new Date(null),
			endDate: new Date(),
		},
	});

	const [showWarningAlert, WarningAlert] = useAlert();
	const [showJudgeDateAlert, JudgeDateAlert] = useAlert();
	const [showShareAlert, ShareAlert] = useAlert();

	const openEvalSettingModal = (idx, d) => {
		if (d.setting) {
			setTidx(idx);
			setFin(true);
			return;
		}
		setTargetRoundIdx(idx);
		setEvaluateSetupModal(true);
	};

	const JudgeClose = (idx) => {
		showWarningAlert().then(async () => {
			await judgeEndRequest.call({ roundIdx: idx });
			await window.location.reload();
		});
	};

	const getDday = (endDate) => {
		endDate = moment(endDate).hour(0).minute(0).second(0);
		const currDate = moment(new Date()).hour(0).minute(0).second(0);
		const Dday = endDate.diff(currDate, "days");

		if (Dday === -1) {
			return "D-day";
		} else {
			return "D-" + (Dday + 1);
		}
	};

	async function urlEncryption(idx) {
		const url = cryptoData.enctypt({ idx: idx });
		if (url.includes("+")) {
			return urlEncryption(idx);
		} else {
			return await url;
		}
	}

	const copyClipBoard = (idx) => {
		try {
			urlEncryption(idx).then(async (url) => {
				await navigator.clipboard.writeText(
					`https://startup.gitbal.io/externalRound?idx=${url}`
				);
			});
			showShareAlert();
		} catch (error) {
			console.log("error > ", error);
			alert(t("pages.round.RoundManage.List.alert"));
		}
	};

	const handleSubmit = (data) => {
		const submitData = {
			...formData,
			...data,
			page: 1,
		};

		setFormData(submitData);
		roundListRequest.call(submitData);
	};

	const onRoundsubmit = (d) => {
		const submitData = {
			title: d.title,
		};

		handleSubmit(submitData);
	};

	const formDate = searchForm.watch("dateType");

	const clickDateType = (e) => {
		const selectedDate = e.target.value;

		if (formDate === selectedDate) {
			searchForm.setValue("dateType", "");
		} else {
			const currentDate = new Date();
			let startDate = "";
			let endDate = "";

			switch (selectedDate) {
				case "1":
					startDate = new Date(null);
					endDate = currentDate;
					setDateReadOnly(true);
					setFormData({ ...formData, startDate: startDate, endDate: endDate });
					break;
				case "2":
					const threeMonthAgo = new Date(currentDate);
					threeMonthAgo.setMonth(currentDate.getMonth() - 3);
					startDate = threeMonthAgo;
					endDate = currentDate;
					setDateReadOnly(true);
					setFormData({ ...formData, startDate: startDate, endDate: endDate });
					break;
				case "3":
					const sixMonthAgo = new Date(currentDate);
					sixMonthAgo.setMonth(currentDate.getMonth() - 6);
					startDate = sixMonthAgo;
					endDate = currentDate;
					setDateReadOnly(true);
					setFormData({ ...formData, startDate: startDate, endDate: endDate });
					break;
				case "4":
					const oneYearAgo = new Date(currentDate);
					oneYearAgo.setFullYear(currentDate.getFullYear() - 1);
					startDate = oneYearAgo;
					endDate = currentDate;
					setDateReadOnly(true);
					setFormData({ ...formData, startDate: startDate, endDate: endDate });
					break;
				case "5":
					startDate = currentDate;
					endDate = currentDate;
					setDateReadOnly(false);
					break;
				default:
					break;
			}

			searchForm.setValue("startDate", startDate);
			searchForm.setValue("endDate", endDate);
		}
	};

	const handleStatusBtn = (item, info) => {
		if (currentPath.auth !== "edit") {
			showAuthAlert();
			return;
		}
		switch (item) {
			case "check":
				viewDetail(info.idx);
				break;
			case "edit":
				navigate(`/round/register?idx=${info.idx}`);
				break;
			case "setting":
				openEvalSettingModal(info.idx, info);
				break;
			case "result":
				viewEvalPage(info.idx);
				break;
			case "extra":
				setAddJudgeModal(true);
				setAddEvalTarget(info.idx);
				break;
			case "close":
				JudgeClose(info.idx);
				break;
			case "finalResult":
				navigate(`/round/result/round/${info.idx}`);
				break;
			default:
				break;
		}
	};

	const getBtnList = (item, info) => {
		if (item === "extra") {
			if (!info.prevIdx && !parentRoundList.includes(info.idx))
				return (
					<button
						type="button"
						className="btn__outline angled"
						color="primary"
						onClick={() => handleStatusBtn(item, info)}
					>
						<RoundCopyIcon />
						{ROUND_BTN_TEXT[item]}
					</button>
				);
			return;
		}
		return (
			<button
				type="button"
				className="btn__outline angled"
				color="primary"
				onClick={() => handleStatusBtn(item, info)}
			>
				{ROUND_BTN_TEXT[item]}
			</button>
		);
	};

	return (
		<>
			<div className="c__card search-wrap-card">
				<Form useForm={searchForm}>
					<Group label="등록일" className="datePicker-group">
						<Radio
							name="dateType"
							options={dateTypeOptions}
							handleClick={clickDateType}
							className="c__switch"
						/>
						<DatePicker
							name="startDate"
							readonly={dateReadOnly}
							onChange={(date) => {
								setFormData({ ...formData, startDate: date });
							}}
						/>
						<span className="dash"> - </span>
						<DatePicker
							name="endDate"
							readonly={dateReadOnly}
							onChange={(date) => {
								setFormData({ ...formData, endDate: date });
							}}
						/>
					</Group>
					<Group label="지원 프로그램" className="round-title-wrap">
						<Input
							name="title"
							placeholder="지원 프로그램 제목을 입력해주세요."
							onEnter={searchForm.handleSubmit(onRoundsubmit)}
						/>
						<button
							color="primary"
							className="btn__solid angled btn-search"
							onClick={searchForm.handleSubmit(onRoundsubmit)}
						>
							검색
						</button>
					</Group>
				</Form>
			</div>
			{loading ? (
				<LoadingPage />
			) : parentList && parentList.length > 0 ? (
				<ul className="c__card round-management-list">
					{parentList?.map((d, idx) => {
						const putChildList = childRoundList.filter((fil) => fil.prevIdx === d.idx);
						return (
							<li className="rounding-item" key={idx}>
								<div className="rounding-wrap">
									<div className="rounding-left">
										<div
											className={`img-wrap ${
												d.status === "심사마감" || d.status === "종료"
													? "disabled"
													: ""
											}`}
										>
											<img
												src={
													d.img ||
													(theme === "dark"
														? darkRoundThumb
														: lightRoundThumb)
												}
												alt={t("pages.round.RoundManage.List.imageAlt")}
											/>
										</div>
										<div>
											<div className="rounding-title">
												<p>{d.title}</p>
											</div>
											<div className="rounding-info">
												<span>
													{t("pages.round.RoundManage.List.applyCount")}{" "}
													{d.applyCount}
												</span>
												<span>
													{t("pages.round.RoundManage.List.applyDate")}{" "}
													{dateToString(d?.regDate, "yyyy-MM-dd")}
												</span>
												<button
													type="button"
													className="btn-share"
													onClick={() => {
														if (currentPath.auth !== "edit") {
															showAuthAlert();
															return;
														}
														copyClipBoard(d.idx);
													}}
												>
													<ShareIcon />
													share
												</button>
											</div>
											<div className="rounding-btn-group">
												{ROUND_BTN_STATUS[d.status].map((item) =>
													getBtnList(item, d)
												)}
											</div>
										</div>
									</div>
									<div className="rounding-right">
										{/*{d.setting ? (*/}
										{/*	<></>*/}
										{/*) : (*/}
										{/*	<div className="round-setting-wrap">*/}
										{/*		<h4 className="title">심사 설정 필요</h4>*/}
										{/*		<p className="desc">*/}
										{/*			심사 설정을 완료해야 지원 프로그램이 노출됩니다.*/}
										{/*		</p>*/}
										{/*	</div>*/}
										{/*)}*/}
										<div className="rounding-progress">
											<p className="method">
												{d.applyMethod === "온라인 질의 작성하기"
													? "온라인 접수(깃발)"
													: d.applyMethod === "온라인" ||
													  d.applyMethod === "외부링크연결"
													? "온라인 접수(외부)"
													: d.applyMethod}
											</p>
											<p className="period">
												{t("pages.round.RoundManage.List.roundPeriod")}{" "}
												{dateToString(d?.startDate, "yyyy-MM-dd")} ~{" "}
												{dateToString(d?.endDate, "yyyy-MM-dd")}
											</p>
										</div>
										<If condition={d.status}>
											<Case condition={"심사중"}>
												<div className="rounding-status active">
													{d.status}
												</div>
											</Case>
											<Case condition={"오픈대기"}>
												<div className="rounding-status d-day">
													<div className="waiting">
														{t("pages.round.RoundManage.List.status01")}
													</div>
													{getDday(d.startDate)}
												</div>
											</Case>
											<Case condition={"진행중"}>
												<div className="rounding-status d-day">
													<div className="waiting">
														{t("pages.round.RoundManage.List.status02")}
													</div>
													{getDday(d.endDate)}
												</div>
											</Case>
											<OtherWise>
												<div className="rounding-status">{d.status}</div>
											</OtherWise>
										</If>
									</div>
								</div>
								{/* 2차 추가 심사 */}
								{putChildList.length > 0 &&
									putChildList.map((d, idx) => (
										<div className="rounding-wrap" key={idx}>
											<div className="rounding-left">
												<div
													className={`img-wrap ${
														d.status === "심사마감" ||
														d.status === "종료"
															? "disabled"
															: ""
													}`}
												>
													<img
														src={
															d.img ||
															(theme === "dark"
																? darkRoundThumb
																: lightRoundThumb)
														}
														alt={t(
															"pages.round.RoundManage.List.imageAlt"
														)}
													/>
												</div>
												<div>
													<div className="rounding-title">
														<p>{d.title}</p>
													</div>
													<div className="rounding-info">
														<span>
															{t(
																"pages.round.RoundManage.List.applyCount"
															)}{" "}
															{d.applyCount}
														</span>
														<span>
															{t(
																"pages.round.RoundManage.List.applyDate"
															)}
															{d.regDate.substring(0, 10)}
														</span>
														<button
															type="button"
															className="btn-share"
															onClick={() => {
																if (currentPath.auth !== "edit") {
																	showAuthAlert();
																	return;
																}
																copyClipBoard(d.idx);
															}}
														>
															<ShareIcon />
															share
														</button>
													</div>
													<div className="rounding-btn-group">
														{ROUND_BTN_STATUS[d.status].map((item) =>
															getBtnList(item, d)
														)}
													</div>
												</div>
											</div>
											<div className="rounding-right">
												<div className="rounding-progress">
													{d.setting ? (
														<></>
													) : (
														<div className="round-setting-wrap">
															<h4 className="title">
																심사 설정 필요
															</h4>
															<p className="desc">
																심사 설정을 완료해야 라운드가
																노출됩니다.
															</p>
														</div>
													)}
													<p className="method">
														{d.applyMethod === "온라인 질의 작성하기"
															? "온라인 접수(깃발)"
															: d.applyMethod === "온라인" ||
															  d.applyMethod === "외부링크연결"
															? "온라인 접수(외부)"
															: d.applyMethod}
													</p>
													<p className="period">
														{t(
															"pages.round.RoundManage.List.roundPeriod"
														)}{" "}
														{d.startDate.substring(0, 10)} ~{" "}
														{d.endDate.substring(0, 10)}
													</p>
												</div>
												<If condition={d.status}>
													<Case condition={"심사중"}>
														<div className="rounding-status active">
															{d.status}
														</div>
													</Case>
													<Case condition={"진행중"}>
														<div className="rounding-status d-day">
															<div className="waiting">
																{t(
																	"pages.round.RoundManage.List.status02"
																)}
															</div>
															{getDday(d.endDate)}
														</div>
													</Case>
													<Case condition={"오픈대기"}>
														<div className="rounding-status d-day">
															<div className="waiting">
																{t(
																	"pages.round.RoundManage.List.status01"
																)}
															</div>
															{getDday(d.startDate)}
														</div>
													</Case>
													<OtherWise>
														<div className="rounding-status">
															{d.status}
														</div>
													</OtherWise>
												</If>
											</div>
										</div>
									))}
							</li>
						);
					})}
					<Pagination
						currentPage={formData.page}
						totalPage={totalPage}
						setPageNumber={(number) => {
							if (typeof number === "number")
								return setFormData((prev) => ({
									...prev,
									page: number,
								}));
							setFormData((prev) => ({
								...prev,
								page: number(prev.page),
							}));
						}}
					/>
				</ul>
			) : (
				<div className="c__noData">
					<CautionIcon />
					<p className="main">{t("pages.round.RoundManage.List.noData")}</p>
					<div className="noData_content">
						<p style={{marginBottom: "6px", fontWeight: "500"}}>지원 프로그램을 등록해보세요.</p>
						<ol className={"noData_content_list"}>
							<li>제목, 기간, 내용,이미지, 첨부파일 등 지원 프로그램에 대한 정보를 설정할 수 있습니다.</li>
							<li>지원 프로그램의 심사항목을 설정하고 추가할 수 있습니다.</li>
							<li>
								지원 프로그램의 접수방법을 설정할 수 있습니다.
								<ol className="noData_content_list_sub">
									<li>온라인 질의 : 직접 온라인 질의서를 만들어 심사에 필요한 답변을 확인 가능합니다.</li>
									<li>온라인 : 외부 링크를 올려 지원 기업을 모집할 수 있습니다.</li>
									<li>오프라인 : 접수 장소의 주소를 등록할 수 있습니다.</li>
								</ol>
							</li>
							<li>심사위원 배정 : 해당 지원 프로그램에 등록할 심사위원을 내부, 외부 모두 등록할 수 있습니다.</li>
						</ol>
						<p>- 깃발의 지원 프로그램은 지원자가 깃발 회원이 아닐 경우, 신청에 제한이 있을 수 있습니다.</p>
					</div>
				</div>
			)}
			<div ref={ref}></div>
			{/*--------------------------- 라운드 확인*/}
			{/*----라운드 확인 모달*/}
			<RoundDetail
				roundDetailData={roundDetailData}
				roundDetailModal={roundDetailModal}
				setInquiryPreModal={setInquiryPreModal}
				setRoundDetailModal={setRoundDetailModal}
			/>
			{/*질의항목 미리보기 모달*/}
			<RoundQuest
				roundQuestList={roundDetailData?.query}
				inquiryPreModal={inquiryPreModal}
				setInquiryPreModal={setInquiryPreModal}
				inquiryOutputForm={inquiryOutputForm}
			/>
			{/*--------------------------- 심사설정*/}
			<ModalEvalSetting {...{ evaluateSetupModal, setEvaluateSetupModal }} />
			<ModalFinish />
			{/*--------------------------- 심사마감*/}
			<WarningAlert
				type="warning"
				layout="horiz"
				message={
					<>
						<h3>{t("pages.round.RoundManage.List.warningAlertTitle")}</h3>
						<p>{t("pages.round.RoundManage.List.warningAlertText")}</p>
					</>
				}
				reject={t("pages.round.RoundManage.List.reject")}
				confirm={t("pages.round.RoundManage.List.confirm01")}
				height={230}
			/>
			{/*--------------------------- 추가심사*/}
			<ModalAdditionEval />
			{/*.addCriteriaSetup Alert 모달*/}
			<JudgeDateAlert
				type="warning"
				message={t("pages.round.RoundManage.List.judgeAlertText")}
				confirm={t("pages.round.RoundManage.List.confirm02")}
			/>
			{/*.judgeSetup Alert 모달*/}
			<ShareAlert
				type="success"
				message={t("pages.round.RoundManage.List.shareAlertText")}
				confirm={t("pages.round.RoundManage.List.confirm02")}
			/>
		</>
	);
};

export default RoundManageAll;
